class SlideshowSlideComponent extends HTMLElement {
  constructor() {
    super();
    this.viewport = this.querySelector('.slideshow-slide__viewport');
    this.track = this.querySelector('.slideshow-slide__track');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.progressSegments = this.querySelectorAll('.slideshow-slide__progress-segment');

    if (!this.track || !this.viewport) return;

    this.slides = Array.from(
      this.track.querySelectorAll('.slideshow-slide__slide:not(.slideshow-slide__slide--clone)')
    );

    if (this.slides.length < 2) {
      this.currentIndex = 1;
      this.syncSlideWidths();
      this.setSlideVisibility();
      this.initButtonClicks();
      this.initButtonHoverStates();
      this.updateMediaBounds();
      this.initHeroBannerScrollGuard();
      return;
    }

    this.setupClones();
    this.currentIndex = 1;
    this.isAnimating = false;
    this.isAutoplay = this.track.dataset.autoplay === 'true';
    this.autoplaySpeed = Number(this.track.dataset.speed) * 1000;

    this.prevButton?.addEventListener('click', () => this.goTo('prev'));
    this.nextButton?.addEventListener('click', () => this.goTo('next'));
    this.onTransitionEndHandler = this.onTransitionEnd.bind(this);
    this.track.addEventListener('transitionend', this.onTransitionEndHandler);

    this.progressSegments.forEach((segment) => {
      segment.addEventListener('click', () => {
        const targetIndex = Number(segment.dataset.index);
        this.goToIndex(targetIndex);
      });

      segment.querySelector('.slideshow-slide__progress-fill')?.addEventListener(
        'animationend',
        this.onProgressComplete.bind(this)
      );
    });

    this.initTouch();
    this.initButtonClicks();
    this.initButtonHoverStates();
    this.syncSlideWidths();
    this.setPosition(false);
    this.setSlideVisibility();
    this.startProgress();
    this.updateMediaBounds();

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.viewport);

    this.mediaQuery = window.matchMedia('(min-width: 750px)');
    this.onBreakpointChange = () => this.handleResize();
    this.mediaQuery.addEventListener('change', this.onBreakpointChange);

    window.addEventListener('load', () => {
      this.syncSlideWidths();
      this.setPosition(false);
      this.updateMediaBounds();
      this.syncHeroBannerContentVisibility();
    });

    this.initHeroBannerScrollGuard();
  }

  initHeroBannerScrollGuard() {
    const heroSection = this.closest('[data-header-hero-slideshow]');
    if (!heroSection) return;

    this.heroBannerScrollGuard = () => this.syncHeroBannerContentVisibility();

    window.addEventListener('scroll', this.heroBannerScrollGuard, { passive: true });
    window.addEventListener('resize', this.heroBannerScrollGuard);
    document.addEventListener('shopify:section:load', this.heroBannerScrollGuard);

    this.syncHeroBannerContentVisibility();
  }

  syncHeroBannerContentVisibility() {
    const heroSection = this.closest('[data-header-hero-slideshow]');
    if (!heroSection) return;

    const contents = heroSection.querySelectorAll('.hero-slide__content');
    const isDesktopHero = window.matchMedia('(min-width: 990px)').matches;

    if (!isDesktopHero) {
      contents.forEach((content) => content.classList.remove('is-hero-banner-content-hidden'));
      return;
    }

    const header = heroSection.querySelector('.header-hero-slideshow__header');
    if (!header) return;

    const headerRect = header.getBoundingClientRect();

    contents.forEach((content) => {
      let overlapsHeader = false;

      content.querySelectorAll('.banner__heading, .banner__text, .banner__buttons').forEach((part) => {
        const partRect = part.getBoundingClientRect();
        if (partRect.height <= 0) return;
        if (partRect.bottom > headerRect.top + 2 && partRect.top < headerRect.bottom - 2) {
          overlapsHeader = true;
        }
      });

      content.classList.toggle('is-hero-banner-content-hidden', overlapsHeader);
    });
  }

  syncSlideWidths() {
    if (!this.track || !this.viewport) return;

    const width = this.viewport.clientWidth;
    this.track.querySelectorAll('.slideshow-slide__slide').forEach((slide) => {
      slide.style.flex = '0 0 ' + width + 'px';
      slide.style.width = width + 'px';
      slide.style.maxWidth = width + 'px';
    });
  }

  getActiveSlide() {
    return this.slides[this.getRealIndex()] || this.slides[0];
  }

  getActiveButtons() {
    const activeSlide = this.getActiveSlide();
    if (!activeSlide) return [];

    return Array.from(
      activeSlide.querySelectorAll('.banner__buttons a[href]:not([aria-disabled="true"])')
    ).filter((link) => link.getAttribute('href') && link.getAttribute('href') !== '#');
  }

  getActiveMediaLink() {
    const activeSlide = this.getActiveSlide();
    if (!activeSlide) return null;

    const mediaLink = activeSlide.querySelector('.hero-slide__media-link[href]');
    if (mediaLink?.getAttribute('href') && mediaLink.getAttribute('href') !== '#') return mediaLink;

    return this.getActiveButtons()[0] || null;
  }

  getLinkAtPoint(x, y) {
    const button = this.getButtonAtPoint(x, y);
    if (button) return button;

    const activeSlide = this.getActiveSlide();
    if (!activeSlide) return null;

    const mediaFrame = activeSlide.querySelector('.slideshow-slide__media-frame--linked, .slideshow-slide__media-frame:has(.hero-slide__media-link)');
    if (!mediaFrame) return null;

    const rect = mediaFrame.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    if (!this.isPointInsideRect(x, y, rect)) return null;

    return this.getActiveMediaLink();
  }

  isPointInsideRect(x, y, rect) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  getButtonAtPoint(x, y) {
    const buttons = this.getActiveButtons();

    for (const link of buttons) {
      const rect = link.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) continue;
      if (this.isPointInsideRect(x, y, rect)) return link;
    }

    return null;
  }

  clearButtonHover() {
    this.hoveredButton?.classList.remove('is-button-hovered');
    this.hoveredButton = null;
    if (this.viewport) this.viewport.style.cursor = '';
  }

  setHoveredButton(button) {
    if (this.hoveredButton === button) return;

    this.hoveredButton?.classList.remove('is-button-hovered');
    this.hoveredButton = button || null;
    this.hoveredButton?.classList.add('is-button-hovered');
    if (this.viewport) this.viewport.style.cursor = button ? 'pointer' : '';
  }

  initButtonClicks() {
    this.hoveredButton = null;

    this.onButtonClick = (event) => {
      if (event.target.closest('.slideshow-slide__arrow, .slideshow-slide__progress-segment')) return;
      if (event.target.closest('.hero-slide__media-link, .banner__buttons a')) return;

      const link = this.getLinkAtPoint(event.clientX, event.clientY);
      if (!link) return;

      event.preventDefault();
      event.stopPropagation();
      window.location.assign(link.href);
    };

    this.onButtonHover = (event) => {
      const link = this.getLinkAtPoint(event.clientX, event.clientY);
      const button = link?.classList?.contains('button') ? link : this.getButtonAtPoint(event.clientX, event.clientY);

      this.setHoveredButton(button);

      if (this.viewport) {
        this.viewport.style.cursor = link ? 'pointer' : button ? 'pointer' : '';
      }
    };

    this.onButtonLeave = () => {
      this.clearButtonHover();
    };

    this.addEventListener('click', this.onButtonClick, true);
    this.addEventListener('mousemove', this.onButtonHover);
    this.addEventListener('mouseleave', this.onButtonLeave);
  }

  initButtonHoverStates() {
    this.querySelectorAll('.banner__buttons .button').forEach((button) => {
      button.addEventListener('mouseenter', () => this.setHoveredButton(button));
      button.addEventListener('mouseleave', () => {
        if (this.hoveredButton === button) this.clearButtonHover();
      });
      button.addEventListener('focusin', () => this.setHoveredButton(button));
      button.addEventListener('focusout', () => {
        if (this.hoveredButton === button) this.clearButtonHover();
      });
    });
  }

  handleResize() {
    if (!this.track || !this.viewport) return;

    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);

    this.resizeRaf = requestAnimationFrame(() => {
      this.isAnimating = false;
      clearTimeout(this.animationFallbackTimeout);
      this.syncSlideWidths();
      this.setPosition(false);

      requestAnimationFrame(() => {
        this.updateMediaBounds();
        this.syncHeroBannerContentVisibility();

        if (this.slides.length >= 2) {
          this.startProgress();
        }
      });
    });
  }

  clearAnimationLock() {
    this.isAnimating = false;
    clearTimeout(this.animationFallbackTimeout);
  }

  setAnimationLock() {
    this.isAnimating = true;
    clearTimeout(this.animationFallbackTimeout);
    this.animationFallbackTimeout = setTimeout(() => {
      this.isAnimating = false;
    }, 600);
  }

  updateMediaBounds() {
    if (!this.viewport || !this.slides.length) return;

    const activeIndex = this.slides.length === 1 ? 0 : this.getRealIndex();
    const activeSlide = this.slides[activeIndex];
    if (!activeSlide) return;

    const mediaFrame = activeSlide.querySelector('.slideshow-slide__media-frame');
    if (!mediaFrame) return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const mediaRect = mediaFrame.getBoundingClientRect();

    this.viewport.style.setProperty('--slideshow-media-top', (mediaRect.top - viewportRect.top) + 'px');
    this.viewport.style.setProperty('--slideshow-media-height', mediaRect.height + 'px');
    this.syncHeroBannerContentVisibility();
  }

  setupClones() {
    const firstClone = this.slides[0].cloneNode(true);
    const lastClone = this.slides[this.slides.length - 1].cloneNode(true);

    firstClone.classList.add('slideshow-slide__slide--clone');
    lastClone.classList.add('slideshow-slide__slide--clone');
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');
    firstClone.removeAttribute('id');
    lastClone.removeAttribute('id');

    this.track.insertBefore(lastClone, this.slides[0]);
    this.track.appendChild(firstClone);
  }

  getSlideWidth() {
    return this.viewport.clientWidth;
  }

  setPosition(animate = true) {
    this.syncSlideWidths();
    const offset = this.currentIndex * this.getSlideWidth();

    if (!animate) {
      this.track.classList.add('slideshow-slide__track--no-transition');
    }

    this.track.style.transform = 'translate3d(-' + offset + 'px, 0, 0)';

    if (!animate) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.classList.remove('slideshow-slide__track--no-transition');
        });
      });
    }
  }

  goTo(direction) {
    if (this.isAnimating) return;

    this.setAnimationLock();
    this.currentIndex += direction === 'next' ? 1 : -1;
    this.setPosition(true);
    this.resetProgress();
    requestAnimationFrame(() => this.updateMediaBounds());
  }

  goToIndex(targetIndex) {
    const current = this.getRealIndex();
    if (targetIndex === current || this.isAnimating) return;

    this.setAnimationLock();
    this.currentIndex = targetIndex + 1;
    this.setPosition(true);
    this.resetProgress();
  }

  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;

    this.clearAnimationLock();
    const total = this.slides.length;

    if (this.currentIndex === 0) {
      this.currentIndex = total;
      this.setPosition(false);
    } else if (this.currentIndex === total + 1) {
      this.currentIndex = 1;
      this.setPosition(false);
    }

    this.setSlideVisibility();
    this.startProgress();
    this.updateMediaBounds();
    this.syncHeroBannerContentVisibility();
  }

  getRealIndex() {
    const total = this.slides.length;
    if (this.currentIndex <= 0) return total - 1;
    if (this.currentIndex > total) return 0;
    return this.currentIndex - 1;
  }

  resetProgress() {
    clearTimeout(this.autoplayTimeout);

    this.progressSegments.forEach((segment) => {
      const fill = segment.querySelector('.slideshow-slide__progress-fill');
      if (!fill) return;

      fill.classList.remove('is-animating', 'is-complete', 'is-active');
      fill.style.animationPlayState = '';
      fill.style.removeProperty('--progress-duration');
    });
  }

  startProgress() {
    if (!this.progressSegments.length) return;

    clearTimeout(this.autoplayTimeout);
    const activeIndex = this.getRealIndex();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.progressSegments.forEach((segment, index) => {
      const fill = segment.querySelector('.slideshow-slide__progress-fill');
      if (!fill) return;

      fill.classList.remove('is-animating', 'is-complete', 'is-active');
      fill.style.animationPlayState = '';
      segment.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');

      if (index < activeIndex) {
        fill.classList.add('is-complete');
      } else if (index === activeIndex) {
        fill.classList.add('is-active');

        if (this.isAutoplay) {
          fill.style.setProperty('--progress-duration', this.autoplaySpeed + 'ms');

          if (prefersReducedMotion) {
            fill.classList.add('is-complete');
          } else {
            fill.classList.add('is-animating');
          }
        } else {
          fill.classList.add('is-complete');
        }
      }
    });

    if (this.isAutoplay && prefersReducedMotion) {
      this.autoplayTimeout = setTimeout(() => {
        if (!this.isAnimating) this.goTo('next');
      }, this.autoplaySpeed);
    }
  }

  onProgressComplete(event) {
    if (!event.target.classList.contains('slideshow-slide__progress-fill')) return;
    if (!event.target.classList.contains('is-animating')) return;
    if (!this.isAutoplay || this.isAnimating) return;

    this.goTo('next');
  }

  setSlideVisibility() {
    this.clearButtonHover();

    const activeIndex = this.getRealIndex();
    const allSlides = this.track.querySelectorAll('.slideshow-slide__slide');

    allSlides.forEach((slide) => {
      const isClone = slide.classList.contains('slideshow-slide__slide--clone');
      const slideIndex = this.slides.indexOf(slide);
      const isActive = !isClone && slideIndex === activeIndex;

      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.setAttribute('tabindex', isActive ? '0' : '-1');

      if (isClone) return;

      slide.querySelectorAll('a').forEach((link) => {
        link.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    });
  }

  initTouch() {
    let startX = 0;
    let isDragging = false;

    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        if (event.target.closest('.slideshow-slide__arrow, .slideshow-slide__progress-segment, button, .banner__buttons a')) return;
        startX = event.touches[0].clientX;
        isDragging = true;
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchend',
      (event) => {
        if (event.target.closest('.slideshow-slide__arrow, .slideshow-slide__progress-segment, button, .banner__buttons a')) return;
        if (!isDragging) return;
        isDragging = false;

        const diff = startX - event.changedTouches[0].clientX;
        if (Math.abs(diff) < 40) return;

        this.goTo(diff > 0 ? 'next' : 'prev');
      },
      { passive: true }
    );
  }

  pause() {
    clearTimeout(this.autoplayTimeout);

    this.querySelectorAll('.slideshow-slide__progress-fill.is-animating').forEach((fill) => {
      fill.style.animationPlayState = 'paused';
    });
  }

  resume() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.startProgress();
      return;
    }

    this.querySelectorAll('.slideshow-slide__progress-fill.is-animating').forEach((fill) => {
      fill.style.animationPlayState = 'running';
    });
  }
}

if (!customElements.get('slideshow-slide-component')) {
  customElements.define('slideshow-slide-component', SlideshowSlideComponent);
}
